import { useMemo, useState } from 'react';
import { adaptArrayToTableObject } from '../../base/TableManager/TableManager.utils';
import { MEMBER_ASSIGNED_TRAININGS_HEADINGS } from '../../../../lib/constants/courses';
import TableManager from '../../base/TableManager/TableManager';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { useConfig } from '../../../../lib/hooks/config';
import { TeamMemberApiTraining, TeamMemberTrainingsProps } from './TeamMemberTrainings.types';
import { adaptToMemberTraining } from './TeamMemberTrainings.utils';
import { isNil } from '../../../../lib/utils/isNil';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { useUserTrainings } from '../../../../lib/api/swrApi';
import Layout from '../../../common/Layout/Layout';
import { useInternalComponent } from '../../../../lib/context/InternalComponentContext';
import TeamMemberAssignTrainingDialog from './TeamMemberAssignTrainingDialog';
import { NotificationType } from '../../base/Notification/Notification';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { replaceObjectPlaceholders } from '../../../../lib/utils/placeholders/replacePlaceholder';
import useDeleteState from '../../../../lib/hooks/useDeleteState';
import { ConfirmDialog } from '../../../common/ConfirmDialog/ConfirmDialog';
import { TEAM_MEMBER_DETAILS } from '../../../../lib/constants/labelKeys';
import { useDeleteUserFromCourse, useDeleteUserFromLp } from '../../../../lib/api/postApi';
import useFeature from '../../../../lib/hooks/useFeature';
import TeamMemberTrainingsActions from './TeamMemberTrainingsActions';
import styles from './TeamMemberTrainings.module.scss';
import { FeatureToggleCodename } from '../../../../lib/types/enums';
import { Api } from '../../../../lib/types/apiSpec';
import { isCourseType } from '../../../../lib/utils/trainings/typesChecking';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const TeamMemberTrainings = ({
  headingLevel,
  isInEditor,
  dialogTitle,
  noItemsSelectedMessage,
  successMessage,
  buttonLabel,
  removeTrainingSuccessMessage,
  reportConfig,
  reportButtonLabel,
  ...rest
}: TeamMemberTrainingsProps) => {
  const config = useConfig();
  const internal = useInternalComponent();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConfirmModalId, setShowConfirmModalId] = useState<string | null>(null);
  const removeTrainingEnabled = useFeature(FeatureToggleCodename.removeMemberTraining);
  const { page } = useViewContext();
  const { data, error, isLoading, isValidating, mutate: mutateUserTrainings } = useUserTrainings(page.memberId);
  const { triggerSnackbar } = useSnackbarContext();
  const overdueTrainings = useMemo(() => data?.filter(training => training.overdue).map(({ id }) => id), [data]);
  const [trainingsRemovalState, dispatchTrainingsRemoval] = useDeleteState({});
  const deleteLp = useDeleteUserFromLp();
  const deleteCourse = useDeleteUserFromCourse();
  const onBinClickHandler = (id: string) => setShowConfirmModalId(id);
  const { triggerSnackbarError } = useErrorMapping();

  const tableData = useMemo(
    () =>
      data
        ?.map(
          adaptToMemberTraining({
            config,
            onBinClickHandler,
            itemsRemovingState: trainingsRemovalState,
          }),
        )
        .reduce(
          adaptArrayToTableObject(
            MEMBER_ASSIGNED_TRAININGS_HEADINGS.slice(
              0,
              !removeTrainingEnabled ? -1 : undefined,
            ) as (keyof TeamMemberApiTraining)[],
          ),
          {},
        ),
    [data, trainingsRemovalState],
  );

  const handleSuccess = (fullSuccess: boolean = true) => {
    mutateUserTrainings();

    if (fullSuccess) {
      setShowAssignModal(false);
      triggerSnackbar(successMessage, NotificationType.Success);
    }
  };

  const handleConfirmDelete = async (id: string) => {
    if (!page.memberId) {
      return;
    }

    setShowConfirmModalId(null);
    dispatchTrainingsRemoval({ type: 'deleteStateAdd', payload: id });

    const training = data?.find(({ id: trainingId }) => trainingId === id);

    if (!training) {
      return;
    }

    try {
      if (isCourseType(training.type)) {
        await deleteCourse({ userid: page.memberId, courseid: id });
      } else {
        await deleteLp({ userid: page.memberId, lpId: id });
      }
      await mutateUserTrainings();
      triggerSnackbar(removeTrainingSuccessMessage, NotificationType.Success);
    } catch (axiosError: any) {
      triggerSnackbarError(axiosError);
    } finally {
      dispatchTrainingsRemoval({ type: 'deleteStateRemove', payload: id });
    }
  };

  if (error) {
    return <ErrorInfo error={error} />;
  }

  return (
    <Layout className={styles.container}>
      <TeamMemberTrainingsActions
        buttonLabel={buttonLabel}
        reportButtonLabel={reportButtonLabel}
        setShowAssignModal={setShowAssignModal}
        reportConfig={reportConfig}
      />
      <TableManager
        {...rest}
        className={styles.centeredOverdue}
        customClasses={{ ...(!internal ? { headContainer: styles.headContainer } : {}) }}
        data={tableData ?? {}}
        headings={MEMBER_ASSIGNED_TRAININGS_HEADINGS.slice(0, -1)}
        headingLevel={HeadingLevel.h2}
        headingDisplayLevel={headingLevel}
        selected={overdueTrainings}
        isInitialLoad={isLoading}
        isLoading={isValidating && !isNil(data)}
        desktopCenteredColumns={[1, 2, 4]}
        defaultSort={{ column: 2, sortDirection: Api.SortDirection.desc }}
      />
      {showAssignModal && (
        <TeamMemberAssignTrainingDialog
          onSuccess={handleSuccess}
          onCancel={() => setShowAssignModal(false)}
          isModalOpened={showAssignModal}
          dialogTitle={replaceObjectPlaceholders(page.member ?? {}, dialogTitle)}
          noItemsSelectedMessage={noItemsSelectedMessage}
        />
      )}
      {showConfirmModalId && (
        <ConfirmDialog
          onCancel={() => setShowConfirmModalId(null)}
          onConfirm={() => handleConfirmDelete(showConfirmModalId)}
          labelKeys={{
            dialogTitle: TEAM_MEMBER_DETAILS.REMOVE_TRAINING_DIALOG.TITLE,
            content: TEAM_MEMBER_DETAILS.REMOVE_TRAINING_DIALOG.CONTENT,
            confirmButton: TEAM_MEMBER_DETAILS.REMOVE_TRAINING_DIALOG.CONFIRM_BUTTON,
          }}
          isOpen={!!showConfirmModalId}
        />
      )}
    </Layout>
  );
};

export default TeamMemberTrainings;
